import { profile as moonMeetProfile } from './moonmeet/api';
import BrandLogo from './components/BrandLogo';
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Alert, Box, Button, CircularProgress, FormControl, FormLabel, Input, Stack, Typography } from "@mui/joy";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { APILogin, APIMe } from "./api";
 
import EmailRoundedIcon        from "@mui/icons-material/EmailRounded";
import LockRoundedIcon         from "@mui/icons-material/LockRounded";
import VisibilityRoundedIcon    from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import ShieldRoundedIcon        from "@mui/icons-material/ShieldRounded";
import ViewKanbanRoundedIcon    from "@mui/icons-material/ViewKanbanRounded";
 
import ThemeModeControl from "./theme/ThemeModeControl";
import { forgetSession } from "./safe/sessionStore";

export const LayoutContext = createContext();
 
// ─── Цвета бренда ─────────────────────────────────────────────────────────────
const BRAND   = "#686DF3";
const BRAND2  = "#7e82e1";
 
// ─── Плавающие частицы ────────────────────────────────────────────────────────
const PARTICLES = Array.from({ length: 22 }, (_, i) => ({
    id: i,
    size: 2 + Math.random() * 5,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: 7 + Math.random() * 10,
    delay: Math.random() * 7,
    opacity: 0.035 + Math.random() * 0.07,
}));
 
function FloatingParticles() {
    return (
        <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
            {PARTICLES.map(p => (
                <motion.div
                    key={p.id}
                    style={{
                        position: "absolute",
                        left: `${p.x}%`, top: `${p.y}%`,
                        width: p.size, height: p.size,
                        borderRadius: "50%",
                        backgroundColor: BRAND,
                        opacity: p.opacity,
                    }}
                    animate={{
                        y: [0, -28, 0],
                        x: [0, 10, -10, 0],
                        opacity: [p.opacity, p.opacity * 2.8, p.opacity],
                        scale: [1, 1.6, 1],
                    }}
                    transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "easeInOut" }}
                />
            ))}
        </Box>
    );
}
 
// ─── Сетка-фон ────────────────────────────────────────────────────────────────
function GridDecor() {
    return (
        <Box sx={{
            position: "absolute", inset: 0, pointerEvents: "none",
            backgroundImage: `linear-gradient(rgba(87,42,195,0.05) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(87,42,195,0.05) 1px, transparent 1px)`,
            backgroundSize: "56px 56px",
        }} />
    );
}
 
// ─── Карточка с эффектом наклона при наведении мыши ──────────────────────────
function TiltCard({ children }) {
    const ref = useRef(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotateX = useTransform(y, [-0.5, 0.5], [3, -3]);
    const rotateY = useTransform(x, [-0.5, 0.5], [-3, 3]);
 
    const handleMouseMove = (e) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        x.set((e.clientX - rect.left) / rect.width - 0.5);
        y.set((e.clientY - rect.top)  / rect.height - 0.5);
    };
    const handleMouseLeave = () => { x.set(0); y.set(0); };
 
    return (
        <motion.div
            ref={ref}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d", perspective: 800 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
            {children}
        </motion.div>
    );
}
 
// ─── LoginPage ────────────────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
    const [email, setEmail]           = useState("");
    const [pwd, setPwd]               = useState("");
    const [showPass, setShowPass]     = useState(false);
    const [error, setError]           = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading]       = useState(false);
    const [shakeKey, setShakeKey]     = useState(0);
 
    const validate = () => {
        const errs = {};
        if (!email.trim())  errs.email = "Введите email";
        if (!pwd.trim())    errs.pwd   = "Введите пароль";
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    };
 
    const handleSubmit = async () => {
        setError("");
        if (!validate()) { setShakeKey(k => k + 1); return; }
        setLoading(true);
        try {
            await onLogin(email, pwd);
        } catch (e) {
            setError(e?.response?.data?.error || "Неверный email или пароль");
            setShakeKey(k => k + 1);
        } finally {
            setLoading(false);
        }
    };
 
    // Анимации
    const containerVariants = {
        hidden:  {},
        visible: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
    };
    const itemVariants = {
        hidden:  { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
    };
    const shakeVariants = {
        shake: { x: [0, -10, 10, -8, 8, -4, 4, 0], transition: { duration: 0.45 } },
        idle:  { x: 0 },
    };
 
    return (
        <Box sx={{
            minHeight: "100dvh", width: "100%",
            display: "flex", alignItems: "center", justifyContent: "center",
            position: "relative", overflow: "hidden",
            background: "var(--ld-login)",
        }}>
            <Box sx={{ position: "absolute", right: 20, top: 20, zIndex: 2 }}><ThemeModeControl /></Box>
            <GridDecor />
            <FloatingParticles />
 
            {/* Glow blobs */}
            <Box sx={{
                position: "absolute", top: "-20%", left: "-15%",
                width: 560, height: 560, borderRadius: "50%", pointerEvents: "none",
                background: `radial-gradient(circle, rgba(87,42,195,0.1) 0%, transparent 65%)`,
            }} />
            <Box sx={{
                position: "absolute", bottom: "-25%", right: "-10%",
                width: 640, height: 640, borderRadius: "50%", pointerEvents: "none",
                background: `radial-gradient(circle, rgba(124,58,237,0.07) 0%, transparent 65%)`,
            }} />
            {/* Маленький акцентный блоб */}
            <motion.div
                style={{ position: "absolute", top: "30%", right: "15%", pointerEvents: "none" }}
                animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
                <Box sx={{
                    width: 120, height: 120, borderRadius: "50%",
                    background: `radial-gradient(circle, rgba(129,140,248,0.25) 0%, transparent 70%)`,
                }} />
            </motion.div>
 
            {/* Shake wrapper */}
            <motion.div
                key={shakeKey}
                variants={shakeVariants}
                animate={error ? "shake" : "idle"}
                style={{ width: "100%", maxWidth: 420, padding: "0 16px", position: "relative", zIndex: 1 }}
            >
                <motion.div variants={containerVariants} initial="hidden" animate="visible">
 
                    {/* ── Логотип ── */}
                    <motion.div variants={itemVariants}>
                        <Stack alignItems="center" sx={{ mb: 4 }}>
                            <motion.div
                                initial={{ scale: 0, rotate: -25 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ delay: 0.05, type: "spring", stiffness: 260, damping: 18 }}
                            >
                                <BrandLogo size={80} sx={{
                                    width: 80, height: 80, borderRadius: 20,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
                                }} />
                            </motion.div>
 
                            <Typography sx={{
                                fontWeight: 900, fontSize: "1.75rem", letterSpacing: "-0.05em", lineHeight: 1,
                                background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND2} 100%)`, mt: 2,
                                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                            }}>
                                LunaDesk
                            </Typography>
                            <Typography sx={{
                                fontSize: "13px", mt: 0.6, letterSpacing: "0.06em",
                                color: "primary.plainColor", fontWeight: 500,
                                textTransform: "uppercase",
                            }}>
                                Персональный помощник
                            </Typography>
                        </Stack>
                    </motion.div>
 
                    {/* ── Карточка с tilt-эффектом ── */}
                    <motion.div variants={itemVariants}>
                        <TiltCard>
                            <Box sx={{
                                p: { xs: 2.5, sm: 3.5 },
                                borderRadius: "24px",
                                border: "1px solid rgba(87,42,195,0.12)",
                                bgcolor: "var(--ld-glass)",
                                backdropFilter: "blur(24px)",
                                WebkitBackdropFilter: "blur(24px)",
                                boxShadow: "0 24px 64px rgba(87,42,195,0.12), inset 0 1px 0 rgba(255,255,255,0.95)",
                            }}>
                                <Stack spacing={2.5} onKeyDown={e => { if (e.key === "Enter") handleSubmit(); }}>
 
                                    {/* Error */}
                                    <AnimatePresence mode="wait">
                                        {error && (
                                            <motion.div key="err"
                                                initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                                                animate={{ opacity: 1, height: "auto", marginBottom: 4 }}
                                                exit={{   opacity: 0, height: 0, marginBottom: 0 }}
                                                transition={{ duration: 0.22 }}
                                            >
                                                <Alert color="danger" variant="soft" sx={{ borderRadius: "12px", fontSize: "13px" }}>
                                                    {error}
                                                </Alert>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
 
                                    {/* Email */}
                                    <FormControl error={!!fieldErrors.email}>
                                        <FormLabel sx={{
                                            fontSize: "11px", fontWeight: 700,
                                            letterSpacing: "0.06em", textTransform: "uppercase", mb: 0.75,
                                        }}>
                                            Email
                                        </FormLabel>
                                        <Input
                                            type="email" placeholder="you@example.com" autoFocus
                                            value={email}
                                            onChange={e => { setEmail(e.target.value); setFieldErrors(p => ({ ...p, email: "" })); }}
                                            startDecorator={<EmailRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                            sx={{
                                                borderRadius: "12px", fontSize: "14px", py: 1.25,
                                                "--Input-focusedThickness": "1.5px",
                                                "--Input-focusedHighlight": BRAND,
                                                ...(fieldErrors.email && { "--Input-focusedHighlight": "#ef4444" }),
                                            }}
                                        />
                                        <AnimatePresence>
                                            {fieldErrors.email && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                                                    exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.18 }}
                                                >
                                                    <Typography sx={{ fontSize: "11px", color: "#ef4444", mt: 0.5 }}>
                                                        {fieldErrors.email}
                                                    </Typography>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </FormControl>
 
                                    {/* Password */}
                                    <FormControl error={!!fieldErrors.pwd}>
                                        <FormLabel sx={{
                                            fontSize: "11px", fontWeight: 700,
                                            letterSpacing: "0.06em", textTransform: "uppercase", mb: 0.75,
                                        }}>
                                            Пароль
                                        </FormLabel>
                                        <Input
                                            type={showPass ? "text" : "password"} placeholder="••••••••"
                                            value={pwd}
                                            onChange={e => { setPwd(e.target.value); setFieldErrors(p => ({ ...p, pwd: "" })); }}
                                            startDecorator={<LockRoundedIcon sx={{ fontSize: 16, opacity: 0.45 }} />}
                                            endDecorator={
                                                <Box onClick={() => setShowPass(v => !v)}
                                                    sx={{ cursor: "pointer", display: "flex", color: "text.tertiary",
                                                        "&:hover": { color: "text.primary" }, transition: "color 0.15s" }}
                                                >
                                                    {showPass
                                                        ? <VisibilityOffRoundedIcon sx={{ fontSize: 17 }} />
                                                        : <VisibilityRoundedIcon    sx={{ fontSize: 17 }} />
                                                    }
                                                </Box>
                                            }
                                            sx={{
                                                borderRadius: "12px", fontSize: "14px", py: 1.25,
                                                "--Input-focusedThickness": "1.5px",
                                                "--Input-focusedHighlight": BRAND,
                                            }}
                                        />
                                        <AnimatePresence>
                                            {fieldErrors.pwd && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                                                    exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.18 }}
                                                >
                                                    <Typography sx={{ fontSize: "11px", color: "#ef4444", mt: 0.5 }}>
                                                        {fieldErrors.pwd}
                                                    </Typography>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </FormControl>
 
                                    {/* Submit */}
                                    <motion.div whileHover={{ scale: loading ? 1 : 1.02 }} whileTap={{ scale: loading ? 1 : 0.97 }}>
                                        <Button
                                            fullWidth loading={loading}
                                            onClick={handleSubmit}
                                            sx={{
                                                borderRadius: "12px", py: 1.4,
                                                fontSize: "14px", fontWeight: 700, letterSpacing: "0.02em",
                                                background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND2} 100%)`,
                                                color: "#fff", border: "none",
                                                boxShadow: `0 4px 18px rgba(87,42,195,0.35)`,
                                                "&:hover": {
                                                    boxShadow: `0 6px 24px rgba(87,42,195,0.45)`,
                                                },
                                                transition: "all 0.2s",
                                            }}
                                        >
                                            Войти
                                        </Button>
                                    </motion.div>
 
                                </Stack>
                            </Box>
                        </TiltCard>
                    </motion.div>
 
                    {/* ── Подпись внизу ── */}
                    <motion.div variants={itemVariants}>
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mt: 3 }}>
                            <ShieldRoundedIcon sx={{ fontSize: 13, color: "text.tertiary" }} />
                            <Typography sx={{
                                fontSize: "11px", letterSpacing: "0.02em",
                                color: "text.tertiary",
                            }}>
                                Защищённое соединение
                            </Typography>
                        </Stack>
                    </motion.div>
 
                </motion.div>
            </motion.div>
        </Box>
    );
}
 
 
// ─── LayoutProvider ───────────────────────────────────────────────────────────
export default function LayoutProvider({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
 
    // ── Sidebar ──────────────────────────────────────────────────────────────
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // ── Modals ──────────────────────────────────────────────────────────────
    const [shareModal, setShareModal] = useState({ open: false });
    const [modalProfile, setModalProfile] = useState(false);
 
    // ── Auth state ────────────────────────────────────────────────────────────
    const [profile, setProfile]         = useState(null);
    const [token, setToken]             = useState(() => localStorage.getItem("auth_token"));
    const [authLoading, setAuthLoading] = useState(true); // проверяем токен при старте
 
    // Логин
    const login = useCallback(async (email, password) => {
        const data = await APILogin(email, password);
        const { token: newToken, profile: profileData } = data;
        localStorage.setItem("auth_token", newToken);
        setToken(newToken);
        setProfile(profileData);
        return profileData;
    }, []);
 
    // Выход
    const logout = useCallback(() => {
        void forgetSession("*", "logout");
        localStorage.removeItem("auth_token");
        setToken(null);
        setProfile(null);
    }, []);
 
    // Верификация токена при загрузке приложения
    useEffect(() => {
        if (!token) {
            setAuthLoading(false);
            return;
        }
        (location.pathname.startsWith('/moonmeet/join/') ? moonMeetProfile : APIMe)()
            .then(data => setProfile(data))
            .catch(() => {
                void forgetSession("*", "logout");
                localStorage.removeItem("auth_token");
                setToken(null);
            })
            .finally(() => setAuthLoading(false));
    }, []); // только один раз при монтировании
 
    // ── Гостевой роут — показываем без авторизации ────────────────────────────
    const isGuestRoute = (window.location.pathname.startsWith("/share/") || window.location.pathname === "/drawer/share" || window.location.pathname.startsWith("/moonmeet/join/"));
    if (isGuestRoute) {
        return (
            <LayoutContext.Provider value={{ sidebarOpen: false, setSidebarOpen: () => {}, profile, authLoading, token, login, logout, setModalProfile: () => {}, shareModal: {open: false}, setShareModal: () => {} }}>
                {children}
            </LayoutContext.Provider>
        );
    }

    // ── Пока проверяем токен ──────────────────────────────────────────────────
    if (authLoading) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100dvh" }}>
                <CircularProgress size="md" />
            </Box>
        );
    }

    // ── Не авторизован — показываем страницу входа ────────────────────────────
    if (!profile) {
        return <LoginPage onLogin={login} />;
    }
    
    // ── Авторизован — рендерим приложение ────────────────────────────────────
    const values = {
        sidebarOpen, setSidebarOpen,
        profile, setProfile,
        token,
        login,
        logout,

        shareModal, setShareModal,
        modalProfile, setModalProfile,
    };
 
    return (
        <LayoutContext.Provider value={values}>
            {children}
        </LayoutContext.Provider>
    );
}
